/**
 * @fileoverview Route Window Host
 * @description Keeps every visited route mounted so route-level pages behave
 * like minimizable windows. Hidden slots are memoized so they never re-render
 * when other routes update; the active slot swap happens inside a transition
 * to keep the UI responsive on slower devices.
 */

import {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { useWindowManager } from '@/contexts/WindowManagerContext';

interface RouteWindowHostProps {
  excludePrefixes?: string[];
  render: (location: Location) => ReactNode;
}

function titleFromPath(pathname: string): string {
  if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
  const parts = pathname.split('/').filter(Boolean);
  const last = parts[parts.length - 1] ?? 'Page';
  return last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const cacheKey = (loc: Location) => `${loc.pathname}${loc.search}`;

/**
 * One memoized slot. Re-renders ONLY when its `active` flag or its `location`
 * reference changes. The render function is captured once per slot lifetime
 * so updates to the host don't bubble into hidden pages.
 */
interface SlotProps {
  cacheId: string;
  active: boolean;
  location: Location;
  render: (location: Location) => ReactNode;
}
const Slot = memo(
  function Slot({ active, location, render }: SlotProps) {
    return (
      <div
        style={{
          display: active ? 'block' : 'none',
          // Tell the browser it can skip rendering work for hidden slots.
          contentVisibility: active ? 'visible' : 'hidden',
        }}
        aria-hidden={!active}
      >
        {render(location)}
      </div>
    );
  },
  (prev, next) =>
    prev.active === next.active &&
    prev.location === next.location &&
    prev.render === next.render,
);

export function RouteWindowHost({ excludePrefixes = ['/auth'], render }: RouteWindowHostProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { windows, register, restore, isMinimized } = useWindowManager();

  const [cache, setCache] = useState<Map<string, Location>>(() => new Map());

  const isExcluded = useCallback(
    (path: string) => excludePrefixes.some((p) => path.startsWith(p)),
    [excludePrefixes],
  );

  const currentKey = cacheKey(location);

  // Add current location to cache on every navigation (low-priority).
  useEffect(() => {
    if (isExcluded(location.pathname)) return;
    setCache((prev) => {
      if (prev.has(currentKey)) return prev;
      const next = new Map(prev);
      next.set(currentKey, location);
      return next;
    });
  }, [currentKey, location, isExcluded]);

  // Register / sync active and already-open cached routes with the window manager.
  // Important: do NOT blindly re-register every cached route. When a taskbar
  // close removes a route window, the route stays in the mounted cache briefly
  // until this host prunes it. Re-registering all cached routes during that
  // small gap makes the closed chip reappear and forces users to click X
  // multiple times.
  useEffect(() => {
    if (isExcluded(location.pathname)) return;
    const openRoutePaths = new Set(
      windows.filter((w) => w.kind === 'route' && w.path).map((w) => w.path as string),
    );

    startTransition(() => {
      for (const [, loc] of cache) {
        const key = cacheKey(loc);
        if (key !== currentKey && !openRoutePaths.has(key)) continue;

        const id = `route:${cacheKey(loc)}`;
        register({
          id,
          kind: 'route',
          title: titleFromPath(loc.pathname),
          subtitle: loc.search ? loc.search.slice(1).slice(0, 40) : undefined,
          path: key,
        });
      }
    });
  }, [cache, currentKey, location.pathname, isExcluded, register, windows]);

  // Auto-restore current route if it was minimized in storage.
  useEffect(() => {
    const id = `route:${currentKey}`;
    if (isMinimized(id)) restore(id);
  }, [currentKey, isMinimized, restore]);

  // Drop cached entries whose chip was closed in the taskbar.
  useEffect(() => {
    const liveRouteKeys = new Set(
      windows.filter((w) => w.kind === 'route' && w.path).map((w) => w.path as string),
    );
    setCache((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const key of prev.keys()) {
        if (key === currentKey) continue;
        if (!liveRouteKeys.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [windows, currentKey]);

  // Stable identity for the render function so memoized Slots don't churn.
  const stableRender = useMemo(() => render, [render]);

  const slots = useMemo(() => Array.from(cache.entries()), [cache]);

  if (isExcluded(location.pathname)) {
    return <>{render(location)}</>;
  }

  return (
    <>
      {slots.map(([key, loc]) => (
        <Slot
          key={key}
          cacheId={key}
          active={key === currentKey}
          location={loc}
          render={stableRender}
        />
      ))}
    </>
  );
}

export function routePathFromWindowId(id: string): string | null {
  if (!id.startsWith('route:')) return null;
  return id.slice('route:'.length);
}
